import React, { useEffect, useState } from 'react'
import { Container, Row, Col, Card, Button } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import useAuth from '../../context/auth/useAuth'
import { useDispatch, useSelector } from 'react-redux'
import { editCandidateCompetences, getCandidate, getCandidateProfileStatus } from '../../actions/candidatesActions'
import competences from '../../components/competences.json'
import { DndContext, KeyboardSensor, PointerSensor, TouchSensor, closestCorners, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import { CompetenceContainer } from '../../components/competences/CompetenceContainer'
import Swal from 'sweetalert2'
import SubMenuHabilities from '../../components/menus/SubMenuHabilities'
import { useNavigate, useParams } from 'react-router-dom'

export default function Competences() {
    const {id: candidateId} = useParams() || {}
    const { t } = useTranslation()
    const { client } = useAuth()
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { candidate, resultcandidate, candidateStatus } = useSelector(state => state.candidates)

    const [competencesOptions, setCompetencesOptions] = useState({
        initial: competences,
        selected: []
    })

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(TouchSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    )

    function findContainer(id) {
        if (id in competencesOptions) {
            return id
        }

        return Object.keys(competencesOptions).find((key) => competencesOptions[key].find(k => k.id === id))
    }

    const handleDragEnd = (event) => {
        const { active, over } = event
        if (!active || !over) {
            return
        }

        const { id } = active
        const { id: overId } = over

        const activeContainer = findContainer(id)
        const overContainer = findContainer(overId)

        if (!activeContainer || !overContainer || activeContainer !== overContainer) {
            return
        }

        const activeIndex = competencesOptions[activeContainer].findIndex(item => item.id === active.id)
        const overIndex = competencesOptions[overContainer].findIndex(item => item.id === overId)

        if (activeIndex !== overIndex) {
            setCompetencesOptions((items) => ({
                ...items,
                [overContainer]: arrayMove(items[overContainer], activeIndex, overIndex)
            }))
        }
    }

    const handleDragOver = (event) => {
        const { active, over } = event
        if (!active || !over) {
            return
        }

        const { id } = active
        const { id: overId } = over

        // Find the containers
        const activeContainer = findContainer(id)
        const overContainer = findContainer(overId)

        if (!activeContainer || !overContainer || activeContainer === overContainer) {
            return
        }

        setCompetencesOptions((prev) => {
            const activeCompetences = prev[activeContainer]
            const overCompetences = prev[overContainer]

            // Verificar si los arrays de competencias están definidos
            if (!activeCompetences || !overCompetences) {
                return prev
            }

            // Encontrar los índices de los elementos
            const activeIndex = activeCompetences.findIndex(item => item.id === id)
            const overIndex = overCompetences.findIndex(item => item.id === overId)

            // Verificar si los índices son válidos
            if (activeIndex === -1) {
                return prev
            }

            let newIndex
            if (overIndex === -1) {
                // We're at the root droppable of a container
                newIndex = overCompetences.length
            } else {
                const isBelowLastItem = over && overIndex === overCompetences.length - 1
                const modifier = isBelowLastItem ? 1 : 0
                newIndex = overIndex >= 0 ? overIndex + modifier : overCompetences.length
            }

            return {
                ...prev,
                [activeContainer]: [
                    ...prev[activeContainer].filter((item) => item.id !== active.id)
                ],
                [overContainer]: [
                    ...prev[overContainer].slice(0, newIndex),
                    competencesOptions[activeContainer][activeIndex],
                    ...prev[overContainer].slice(newIndex, prev[overContainer].length)
                ]
            }
        })
    }

    const handleEditCompetences = async () => {
        if (competencesOptions.selected.length > 12) {
            Swal.fire({
                showConfirmButton: true,
                icon: 'warning',
                text: t('profile.candidate.maxCompetencesExceeded')
            })
        } else if (competencesOptions.selected.length < 6) {
            Swal.fire({
                showConfirmButton: true,
                icon: 'warning',
                text: t('profile.candidate.minCompetencesNotReached')
            })
        } else {
            let competencesToSend = []

            for await (const competence of competencesOptions.selected) {
                competencesToSend.push(competence.id)
            }
            dispatch(editCandidateCompetences(candidateId || client?.id, competencesToSend))
        }
    }

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: 'instant',
        })
        if (client) {
            dispatch(getCandidate(candidateId || client?.id))
        }
    }, [dispatch, t, client])

    useEffect(() => {
        async function setCompetences() {
            if (candidate && candidate.competences) {
                let userCompetencesUsed = []
                let userInitialCompetences = [...competences]

                for await (const competence of candidate.competences) {
                    if (competences.find(c => c.id === competence)) {
                        userCompetencesUsed.push(competences.find(c => c.id === competence))
                        userInitialCompetences.splice(userInitialCompetences.findIndex(c => c.id === competence), 1)
                    }
                }

                setCompetencesOptions({
                    initial: userInitialCompetences,
                    selected: userCompetencesUsed
                })
            }
        }
        setCompetences()
    }, [candidate])

    useEffect(() => {
        if (resultcandidate) {
            switch (resultcandidate) {
                case 'successEditCompetences':
                    dispatch(getCandidate(candidateId || client?.id))
                    dispatch(getCandidateProfileStatus(candidateId || client?.id))
                    Swal.fire({
                        showConfirmButton: true,
                        showDenyButton: false,
                        confirmButtonText: t('global.great'),
                        icon: 'success',
                        text: t('profile.candidate.successEditCompetences')
                    }).then(() => {
                        if(candidateId){
                            navigate('/admin/candidate/languages/'+candidateId)
                        }else{
                            navigate('/candidate/profile/languages')
                        }
                    })
                    break
                case 'errorEditCompetences':
                    Swal.fire({
                        showConfirmButton: true,
                        icon: 'warning',
                        text: t('profile.candidate.errorEditCompetences')
                    })
                    break
                default:
                    break
            }
        }
        // eslint-disable-next-line
    }, [resultcandidate])

    return (
        <Container className='cuerpo'>
            <Row className='mb-4'>
                <Col className='d-flex align-items-center'>
                    <img src="/images/habilidadesbig.svg" alt="Icono Habilidades" />
                    <h1 className='ms-2 m-0'>{t('menus.skills')}</h1>
                </Col>
            </Row>
            <Row className="mt-2">
                <Col>
                    <Card className='mb-5 sombra lateral'>
                        <Card.Body className='p-0 d-flex'>
                            <div className='d-none d-sm-block submenu-lateral'>
                                <SubMenuHabilities seccion='competences' usertype={candidateId ? 'Superadmin' : 'Candidate'} candidateId={candidateId}/>
                            </div>
                            <div className='px-5 py-3 contenido-datos'>
                                <Row className="mt-5 mb-3">
                                    <Col>
                                        <h3>{t('profile.candidate.competences')}</h3>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        {t('profile.candidate.selectCompetencesDescription')}
                                    </Col>
                                </Row>
                                <Row className='my-5'>
                                    <DndContext sensors={sensors} onDragOver={handleDragOver} onDragEnd={handleDragEnd} collisionDetection={closestCorners}>
                                        <Col xs='6'>
                                            <CompetenceContainer id='initial' items={competencesOptions.initial} title="availableCompetences" />
                                        </Col>
                                        <Col xs='6'>
                                            <CompetenceContainer id='selected' items={competencesOptions.selected} title="chosenCompetences" />
                                        </Col>
                                    </DndContext>
                                </Row>
                                <Row className="my-3 d-flex justify-content-end">
                                    <Col xs='12' lg='5'>
                                        <Button type="submit" variant='primary' className="w-100 my-2" onClick={() => { handleEditCompetences() }}>{candidateStatus === '4.1' ? t('profile.candidate.saveAndContinue') : t('profile.candidate.editCompetences')}</Button>
                                    </Col>
                                </Row>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    )
}
